/**
 * Switcher de idioma — toggles 'es' ⇄ 'en'.
 *
 * - Pre-auth: cambia solo i18next + persiste en localStorage (vía detector).
 * - Post-auth: además persiste a `users.locale` con PATCH /me. La fuente
 *   de verdad post-auth es la BD (Doc 19 §2.1).
 *
 * Tokens visuales según Docs/18-BRAND.md.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount, useMsal } from '@azure/msal-react'

import { apiTokenRequest } from '../auth/msalConfig'
import { patchMeLocale } from '../api/me'
import {
  changeLocale,
  isSupportedLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '../i18n/config'

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation('common')
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0]) ?? null
  const [busy, setBusy] = useState(false)

  const current: SupportedLocale = isSupportedLocale(i18n.language)
    ? i18n.language
    : 'es'

  const handleSwitch = async (next: SupportedLocale) => {
    if (next === current || busy) return
    setBusy(true)
    try {
      await changeLocale(next)
      // Si el usuario está autenticado, persistimos a la BD para que
      // futuros logins respeten su preferencia.
      if (account) {
        try {
          const { accessToken } = await instance.acquireTokenSilent({
            ...apiTokenRequest,
            account,
          })
          await patchMeLocale(accessToken, next)
        } catch (err) {
          // Falla silenciosa: el cambio local ya quedó. La próxima sesión
          // verá la preferencia anterior, no es bloqueante.
           
          console.warn('[i18n] PATCH /me locale failed, kept local only', err)
        }
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="inline-flex overflow-hidden rounded-md border border-line bg-surface text-xs font-ui"
      role="group"
      aria-label={t('actions.switch_language')}
    >
      {SUPPORTED_LOCALES.map((loc) => {
        const active = loc === current
        return (
          <button
            key={loc}
            type="button"
            onClick={() => void handleSwitch(loc)}
            disabled={busy}
            className={
              active
                ? 'bg-brand px-3 py-1.5 text-canvas'
                : 'px-3 py-1.5 text-ink-muted hover:bg-surface-muted hover:text-ink transition-colors'
            }
            aria-pressed={active}
          >
            {loc.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}
